import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowRight, ArrowLeft, CheckCircle2, XCircle, ThermometerSun, Droplets, Wind, Weight, Wallet, Sparkles } from "lucide-react";

const CAD_IMG = "https://images.pexels.com/photos/34047024/pexels-photo-34047024.jpeg";

export default function Materials() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  useEffect(() => { api.get("/materials/public").then((r) => setItems(r.data)); }, []);

  return (
    <div className="anim-in">
      <div className="border-b border-border">
        <div className="container-x py-16">
          <p className="tech-label mb-3">Catálogo · Materiais</p>
          <h1 className="text-5xl lg:text-6xl font-bold tracking-tighter max-w-4xl">
            O material certo <br /><span className="text-brand">para cada projeto.</span>
          </h1>
          <p className="mt-6 text-lg text-muted-foreground max-w-2xl">
            Trabalhamos com uma seleção de termoplásticos técnicos e compósitos. Consulta as fichas ou usa o simulador para descobrir o mais adequado ao teu projeto.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href="#simulador" className="btn-primary" data-testid="materials-simulator-cta"><Sparkles size={14} /> Simulador de material</a>
            <Link to="/pedido-orcamento" className="btn-outline">Pedir orçamento <ArrowRight size={14} /></Link>
          </div>
        </div>
      </div>

      <div className="container-x py-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map((m, i) => (
          <button key={m.slug} onClick={() => setSelected(m)} data-testid={`material-card-${m.slug}`}
            className="group text-left border border-border bg-card p-6 hover:border-foreground transition-colors">
            <div className="flex items-start justify-between">
              <span className="mono text-[10px] uppercase tracking-widest text-muted-foreground">0{i + 1} · Material</span>
              <span className="w-3 h-3 rounded-full" style={{ background: m.color }} />
            </div>
            <h3 className="text-3xl font-bold tracking-tighter mt-6">{m.name}</h3>
            <p className="tech-label mt-1">{m.family}</p>
            <p className="text-sm mt-4">{m.tagline}</p>
            <div className="mt-6 flex items-center gap-3 text-[11px] mono uppercase text-muted-foreground">
              <span className="flex items-center gap-1"><ThermometerSun size={11} /> {m.temp_max_c}ºC</span>
              <span className="flex items-center gap-1">{m.outdoor ? <CheckCircle2 size={11} className="text-emerald-600" /> : <XCircle size={11} className="text-rose-600" />} exterior</span>
              <span>€{"€".repeat(m.price_index - 1)}</span>
            </div>
          </button>
        ))}
      </div>

      {/* Simulator */}
      <MaterialSimulator />

      {/* Detail modal */}
      {selected && <MaterialDetailModal item={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

function MaterialDetailModal({ item, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-background border border-border max-w-2xl w-full max-h-[90vh] overflow-y-auto crosshair-corners" onClick={(e) => e.stopPropagation()}>
        <div className="p-8">
          <div className="flex justify-between items-start">
            <div>
              <p className="tech-label mb-1">{item.family}</p>
              <h2 className="text-4xl font-bold tracking-tighter">{item.name}</h2>
              <p className="text-brand mt-1">{item.tagline}</p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-secondary" data-testid="material-modal-close"><XCircle size={20} /></button>
          </div>
          <p className="mt-6 text-sm leading-relaxed">{item.description}</p>

          <div className="mt-6 grid sm:grid-cols-2 gap-4">
            <Spec k="Temperatura máx." v={`${item.temp_max_c}ºC`} />
            <Spec k="Resistência" v={item.tensile_strength} />
            <Spec k="Flexibilidade" v={item.flexibility} />
            <Spec k="Uso exterior" v={item.outdoor ? "Sim" : "Não recomendado"} />
            <Spec k="Contacto alimentar" v={item.food_safe} />
            <Spec k="Faixa de preço" v={"€".repeat(item.price_index)} />
          </div>

          <div className="mt-6">
            <p className="tech-label mb-2 text-emerald-700">Ideal para</p>
            <div className="flex flex-wrap gap-2">
              {item.use_cases.map((u) => <span key={u} className="text-xs px-2.5 py-1 border border-emerald-300 bg-emerald-50 text-emerald-800">{u}</span>)}
            </div>
          </div>
          {item.not_for?.length > 0 && (
            <div className="mt-4">
              <p className="tech-label mb-2 text-rose-700">Evitar</p>
              <div className="flex flex-wrap gap-2">
                {item.not_for.map((u) => <span key={u} className="text-xs px-2.5 py-1 border border-rose-200 bg-rose-50 text-rose-800">{u}</span>)}
              </div>
            </div>
          )}

          <Link to="/pedido-orcamento" className="btn-primary mt-8 w-full justify-center">
            Pedir orçamento em {item.name} <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </div>
  );
}

const Spec = ({ k, v }) => (
  <div className="border border-border p-3">
    <p className="tech-label">{k}</p>
    <p className="font-medium mt-1 text-sm">{v}</p>
  </div>
);

// ============ SIMULATOR ============
const QUESTIONS = [
  {
    key: "usage", label: "Onde vai ser usada a peça?",
    icon: Wind,
    options: [
      { v: "indoor", label: "Interior (casa, escritório)", answer: { outdoor: false } },
      { v: "outdoor", label: "Exterior (sol, chuva)", answer: { outdoor: true } },
      { v: "mixed", label: "Depende / não sei", answer: { outdoor: false } },
    ],
  },
  {
    key: "strength", label: "Que resistência precisa a peça?",
    icon: Weight,
    options: [
      { v: "low", label: "Decorativa ou visual", answer: { strength: "low" } },
      { v: "medium", label: "Uso normal (aguenta manuseio)", answer: { strength: "medium" } },
      { v: "high", label: "Alta (carga, jigs, indústria)", answer: { strength: "high" } },
    ],
  },
  {
    key: "flex", label: "A peça precisa dobrar ou absorver choque?",
    icon: Droplets,
    options: [
      { v: "none", label: "Não — quero rígida", answer: { flexibility: "none" } },
      { v: "some", label: "Um pouco de flexibilidade", answer: { flexibility: "some" } },
      { v: "high", label: "Sim — tipo borracha", answer: { flexibility: "high" } },
    ],
  },
];

function MaterialSimulator() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  async function submit(finalAnswers) {
    setLoading(true);
    try {
      const { data } = await api.post("/materials/recommend", finalAnswers);
      setResult(data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }

  function pick(opt) {
    const next = { ...answers, ...opt.answer };
    setAnswers(next);
    if (step + 1 < QUESTIONS.length) {
      setStep(step + 1);
    } else {
      submit(next);
    }
  }

  function reset() { setStep(0); setAnswers({}); setResult(null); }

  const q = QUESTIONS[step];

  return (
    <section id="simulador" className="border-t border-border bg-foreground text-background">
      <div className="container-x py-20">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-4">
            <p className="tech-label mb-3 text-background/60">Simulador · 3 perguntas</p>
            <h2 className="text-4xl lg:text-5xl font-bold tracking-tighter">
              Qual material <br /><span className="text-brand">para o teu projeto?</span>
            </h2>
            <p className="mt-6 text-background/70 text-sm leading-relaxed">
              Responde a 3 perguntas rápidas e recomendamos o material mais adequado, com base na utilização, resistência e flexibilidade.
            </p>
            {(step > 0 || result) && (
              <button onClick={reset} className="mt-8 mono text-[10px] uppercase tracking-widest text-background/60 hover:text-background flex items-center gap-2" data-testid="simulator-restart">
                <ArrowLeft size={12} /> Recomeçar
              </button>
            )}
          </div>
          <div className="lg:col-span-8">
            <div className="border border-background/20 bg-background/5 backdrop-blur p-8 min-h-[380px] crosshair-corners">
              {!result ? (
                <>
                  <div className="flex items-center gap-2 mono text-[10px] uppercase tracking-widest text-background/60 mb-6">
                    {QUESTIONS.map((_, i) => (
                      <span key={i} className={`h-1 flex-1 ${i <= step ? "bg-brand" : "bg-background/20"}`} />
                    ))}
                    <span className="ml-3">{step + 1}/{QUESTIONS.length}</span>
                  </div>
                  <div className="flex items-center gap-3 mb-6">
                    <q.icon size={22} className="text-brand" />
                    <h3 className="text-2xl font-bold tracking-tight">{q.label}</h3>
                  </div>
                  <div className="grid sm:grid-cols-3 gap-3">
                    {q.options.map((opt) => (
                      <button key={opt.v} onClick={() => pick(opt)} data-testid={`simulator-${q.key}-${opt.v}`}
                        className="text-left p-5 border border-background/20 hover:border-brand hover:bg-brand/10 transition-colors">
                        <span className="mono text-[10px] uppercase text-background/50">Opção</span>
                        <p className="mt-2 font-medium text-sm">{opt.label}</p>
                      </button>
                    ))}
                  </div>
                </>
              ) : (
                <div className="anim-in">
                  <p className="tech-label mb-3 text-background/60">Recomendação para o teu projeto</p>
                  <div className="flex items-center gap-4">
                    <span className="w-4 h-4 rounded-full" style={{ background: result.recommended.color }} />
                    <h3 className="text-5xl font-bold tracking-tighter">{result.recommended.name}</h3>
                  </div>
                  <p className="text-brand mt-2">{result.recommended.tagline}</p>
                  <p className="text-sm mt-4 text-background/80 leading-relaxed max-w-2xl">{result.recommended.description}</p>
                  <div className="mt-8 grid sm:grid-cols-3 gap-3">
                    {result.top3.map((m, i) => (
                      <div key={m.slug} className={`border p-4 ${i === 0 ? "border-brand bg-brand/10" : "border-background/20"}`}>
                        <div className="flex items-center justify-between">
                          <span className="mono text-[10px] uppercase text-background/60">0{i + 1} {i === 0 ? "· melhor" : ""}</span>
                          <span className="w-2 h-2 rounded-full" style={{ background: m.color }} />
                        </div>
                        <p className="font-bold text-xl mt-3">{m.name}</p>
                        <p className="text-xs text-background/60 mt-1">{m.tagline}</p>
                      </div>
                    ))}
                  </div>
                  <Link to="/pedido-orcamento" className="btn-primary mt-8" data-testid="simulator-cta-quote">
                    Pedir orçamento em {result.recommended.name} <ArrowRight size={14} />
                  </Link>
                </div>
              )}
              {loading && <p className="tech-label mt-6 text-background/60">a calcular…</p>}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
